import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import SlotMachine from "@/components/SlotMachine";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) {
        setStatus("timeout");
        return;
      }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!clearedRef.current) {
            clearedRef.current = true;
            clear();
          }
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center px-6 py-32 text-center" data-testid="checkout-result">
      {status === "checking" && (
        <>
          <Loader2 className="mb-6 h-12 w-12 animate-spin text-[#a06d34]" />
          <h1 className="text-2xl font-bold text-[#2b2520]">Confirming your payment…</h1>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="mb-6 h-14 w-14 text-[#2e7d32]" strokeWidth={1.5} />
          <h1 className="mb-3 text-4xl font-black text-[#2b2520]">Order confirmed</h1>
          <p className="mb-8 text-neutral-600">
            Thank you for your purchase. Your order is being prepared for discreet
            shipping. A confirmation is available in your account.
          </p>
          <div className="flex gap-3">
            <Link to="/account" data-testid="view-orders-link" className="border border-black/15 px-6 py-3 text-sm font-semibold uppercase tracking-widest text-[#2b2520] hover:border-[#c99a63] hover:text-[#a06d34]">
              View orders
            </Link>
            <Link to="/" className="bg-[#c99a63] px-6 py-3 text-sm font-semibold uppercase tracking-widest text-[#2b2520] hover:bg-[#bb8850]">
              Continue shopping
            </Link>
          </div>
          <SlotMachine sessionId={sessionId} />
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <XCircle className="mb-6 h-14 w-14 text-[#d32f2f]" strokeWidth={1.5} />
          <h1 className="mb-3 text-3xl font-bold text-[#2b2520]">
            {status === "timeout" ? "Still processing" : "Payment not completed"}
          </h1>
          <p className="mb-8 text-neutral-600">
            {status === "timeout"
              ? "Your payment is taking a moment. Check your account shortly for the order status."
              : "Something went wrong with your payment. Please try again."}
          </p>
          <Link to="/cart" className="bg-[#c99a63] px-6 py-3 text-sm font-semibold uppercase tracking-widest text-[#2b2520] hover:bg-[#bb8850]">
            Back to cart
          </Link>
        </>
      )}
    </div>
  );
}
